import { FC } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Button from 'components/button/Button'
import { CompanyDataFieldKeys, CompanyDataInputs, GeneratorStepProps as Props } from '../types'
import classNames from 'classnames/bind'
import EmailFormFields from 'components/form-fields/email/EmailFormFields'
import PhoneFormField from 'components/form-fields/phone/PhoneFormField'
import AddressForm from 'components/form-fields/address-section'
import { AddressFormTypes } from 'components/form-fields/address-section/types'
import { ProducerContactSteps } from 'features/producer-contact/types'
import CompanyNumberFormField from 'components/form-fields/company-number/CompanyNumberFormField'

const CompanyData: FC<Props> = ({ formInput, handleNext }) => {
  // i18n
  const { t } = useTranslation(['generator', 'validation'])

  // React hook form
  const hookForm = useForm<CompanyDataInputs>({
    mode: 'onBlur',
    defaultValues: formInput?.[ProducerContactSteps.COMPANY_DATA] ?? {}
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = hookForm

  const onSubmit = async (data: CompanyDataInputs) => {
    handleNext(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h3 className="margin-bottom-m">{t('progressCompanyData', 'Je bedrijfsgegevens')}</h3>

      <div className="form-row is-2">
        {/* COMPANY NAME */}
        <div className="form-input">
          <label htmlFor="companyName">{t('companyDataCompanyName', 'Bedrijfsnaam')}</label>
          <input
            type="text"
            className={classNames('is-block', { 'has-error': 'companyName' in errors })}
            {...register('companyName', { required: true, minLength: 2 })}
          />
          {errors && errors.companyName && (
            <p className="margin-top-s margin-bottom-none is-red">
              {errors.companyName.type === 'required' && t('validation:required')}
              {errors.companyName.type === 'minLength' &&
                t('validation:min.string', {
                  attribute: t('companyDataCompanyName', 'Bedrijfsnaam'),
                  length: 2
                })}
            </p>
          )}
        </div>

        {/* CONTACT PERSON */}
        <div className="form-input">
          <label htmlFor="contactPerson">{t('companyDataContactPerson', 'Contactpersoon')}</label>
          <input
            type="text"
            className={classNames('is-block', { 'has-error': 'contactPerson' in errors })}
            {...register('contactPerson', { required: true, minLength: 2 })}
          />
          {errors && errors.contactPerson && (
            <p className="margin-top-s margin-bottom-none is-red">
              {errors.contactPerson.type === 'required' && t('validation:required')}
              {errors.contactPerson.type === 'minLength' &&
                t('validation:min.string', {
                  attribute: t('companyDataContactPerson', 'Contactpersoon'),
                  length: 2
                })}
            </p>
          )}
        </div>
      </div>

      <div className="form-row is-2">
        {/* COMPANY VAT NUMBER */}
        <CompanyNumberFormField isRequired {...hookForm} />

        {/* COMPANY ACTIVITY */}
        <div className="form-input">
          <label htmlFor="companyActivity">{t('companyDataCompanyActivity', 'Bedrijfsactiviteit')}</label>
          <input
            type="text"
            className={classNames('is-block', { 'has-error': 'companyActivity' in errors })}
            {...register('companyActivity', { required: true })}
          />
          {errors && errors.companyActivity && (
            <p className="margin-top-s margin-bottom-none is-red">
              {errors.companyActivity.type === 'required' && t('validation:required')}
            </p>
          )}
        </div>
      </div>

      <div className="form-row is-2">
        {/* EMAIL */}
        <EmailFormFields {...hookForm} />

        {/* MOBILE PHONE */}
        <PhoneFormField isRequired {...hookForm} />
      </div>

      <h4 className={'margin-bottom-m margin-top-l'}>{t('companyDataAddressTitle', 'Adres van het bedrijf')}</h4>

      <div className="margin-bottom-l">
        {/* ADDRESS */}
        <AddressForm
          input={formInput?.[ProducerContactSteps.COMPANY_DATA]?.[CompanyDataFieldKeys.COMPANY_ADDRESS]}
          type={AddressFormTypes.COMPANY_ADRESS}
          className="margin-bottom-m"
          register={register}
          errors={errors}
          setValue={setValue}
        />
      </div>

      <Button withGradient isFullWidthMobile>
        {t('nextStep', 'Volgende stap')}
      </Button>
    </form>
  )
}

export default CompanyData
